/**
 * DietSection — calculadora nutricional + macros objetivo + comidas por día.
 *
 * Calculadora (opcional, `data.calculator`): a partir de peso/altura/edad/sexo/
 * actividad calcula el TDEE (Mifflin-St Jeor) y, con el ajuste de objetivo y los
 * gramos de proteína/grasa por kg, deriva el objetivo de kcal + macros. Cuando
 * está activa, esos valores se vuelcan a `data.macros` (el "objetivo").
 *
 * Cada comida puede tener `items` (alimentos del catálogo): sus macros escalan
 * por cantidad y se suman. El bloque "Progreso" compara lo consumido (suma de
 * todos los alimentos del plan) contra el objetivo, para que el coach sepa
 * cuándo completó los requerimientos.
 *
 * Compatibilidad: comidas sin `items` y planes sin `calculator` siguen
 * funcionando — los macros se editan a mano como antes.
 */
import { useEffect, useRef, useState } from "react";
import {
  ensureIds,
  genId,
  scaledItemMacros,
  sumMealMacros,
  type DietActivity,
  type DietAdjustMode,
  type DietAdjustPreset,
  type DietCalculator,
  type DietData,
  type DietFoodItem,
  type DietMeal,
} from "../../../types/plan";
import { loadFoodCatalog, type Food } from "../../../api/foods";
import {
  ACTIVITY_LABELS,
  ADJUST_MODE_LABELS,
  ADJUST_PRESET_LABELS,
  computeDietTargets,
  defaultDietCalculator,
} from "../../../lib/dietCalc";
import { Icon } from "../../../lib/Icon";
import { InlineEditable } from "../InlineEditable";
import { FoodPickerModal } from "../FoodPickerModal";
import { PageHeader, SectionPage } from "./section-shared";

interface DietSectionProps {
  data: DietData;
  onChange: (next: DietData) => void;
  readonly?: boolean;
  /** Botón "Generar dieta con IA". Solo se pasa desde el editor. PDF y
   *  vista pública lo dejan undefined → no se renderiza. */
  onGenerateWithAi?: () => void;
}

const MACRO_ROWS: Array<[string, "kcal" | "protein" | "carbs" | "fat"]> = [
  ["CALORÍAS", "kcal"],
  ["PROTEÍNA", "protein"],
  ["CARBOS", "carbs"],
  ["GRASAS", "fat"],
];

/** Nombres de comida sugeridos al "+ Agregar comida" (en orden del día). */
const STANDARD_MEAL_NAMES = [
  "Desayuno",
  "Media mañana",
  "Almuerzo",
  "Media tarde",
  "Cena",
  "Snack",
  "Pre-entreno",
  "Post-entreno",
];

/** Redondea a entero para mostrar / volcar a los campos string. */
function r(n: number): number {
  return Math.round(n);
}

/** Convierte un Food del catálogo en un DietFoodItem (cantidad inicial = base). */
function foodToItem(food: Food): DietFoodItem {
  return {
    id: genId("fi"),
    libId: food.id > 0 ? food.id : undefined,
    name: food.name,
    qty: food.base_quantity || 100,
    unit: food.base_unit || "g",
    baseQty: food.base_quantity || 100,
    kcal: food.calories,
    protein: food.protein,
    carbs: food.carbs,
    fat: food.fat,
  };
}

/** Parsea un número tolerando unidades en el string: "45g" → 45, "490 kcal"
 *  → 490, "" → 0. `Number()` directo devolvía NaN en estos casos y la suma
 *  daba 0, así el entrenador veía "no me suma los macros". */
function toNum(v: string | number | null | undefined): number {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const m = String(v).trim().replace(",", ".").match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : 0;
}

/** Suma de los macros de UN día (items del catálogo + macros string legacy). */
function sumDayMacros(day: { meals: DietMeal[] }): { kcal: number; p: number; c: number; f: number } {
  const acc = { kcal: 0, p: 0, c: 0, f: 0 };
  for (const m of day.meals || []) {
    if (m.items && m.items.length > 0) {
      const t = sumMealMacros(m.items);
      acc.kcal += t.kcal;
      acc.p += t.p;
      acc.c += t.c;
      acc.f += t.f;
    } else {
      // OJO: usamos toNum (parseFloat tolerante) porque el coach suele escribir
      // "45g" en proteína, "55g" en carbos, etc. Number() directo daba NaN.
      acc.kcal += toNum(m.kcal);
      acc.p += toNum(m.p);
      acc.c += toNum(m.c);
      acc.f += toNum(m.f);
    }
  }
  return acc;
}

/** Suma de TODOS los días del plan — base para el promedio diario del resumen. */
function sumConsumed(data: DietData): { kcal: number; p: number; c: number; f: number } {
  const acc = { kcal: 0, p: 0, c: 0, f: 0 };
  for (const day of data.days) {
    const t = sumDayMacros(day);
    acc.kcal += t.kcal;
    acc.p += t.p;
    acc.c += t.c;
    acc.f += t.f;
  }
  return acc;
}

export function DietSection({ data, onChange, readonly, onGenerateWithAi }: DietSectionProps) {
  /** Comida a la que se le está agregando un alimento (di/mi) — null = picker cerrado. */
  const [pickerTarget, setPickerTarget] = useState<{ di: number; mi: number } | null>(null);

  const calc = data.calculator;
  const targets = calc && calc.enabled ? computeDietTargets(calc) : null;

  // Garantizar IDs estables en meals.
  useEffect(() => {
    if (readonly) return;
    let dirty = false;
    const days = data.days.map((d) => {
      const meals = ensureIds(d.meals || [], "ml");
      if (meals !== d.meals) {
        dirty = true;
        return { ...d, meals };
      }
      return d;
    });
    if (dirty) onChange({ ...data, days });
  }, [data, readonly, onChange]);

  // Resolver macros de items que vienen SOLO con libId (caso plan generado por
  // IA: el modelo manda libId + qty + name, sin baseQty/kcal/protein/carbs/fat
  // para ahorrar tokens). Los completamos desde el catálogo de alimentos por id.
  // Determinístico, no depende del nombre. Tras esto, el efecto de abajo recalcula
  // los totales de cada comida. Solo en editor (readonly nunca muta).
  const foodCatRef = useRef<Food[] | null>(null);
  const [foodCatReady, setFoodCatReady] = useState(false);
  const needsFoodFill = (it: DietFoodItem) =>
    it.libId != null && (!it.baseQty || (!it.kcal && !it.protein && !it.carbs && !it.fat));

  useEffect(() => {
    if (readonly || foodCatRef.current) return;
    const needs = data.days.some((d) =>
      (d.meals || []).some((m) => (m.items || []).some(needsFoodFill)),
    );
    if (!needs) return;
    loadFoodCatalog()
      .then((list) => {
        foodCatRef.current = list;
        setFoodCatReady(true);
      })
      .catch(() => { /* sin catálogo no podemos resolver — quedan en 0 */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, readonly]);

  useEffect(() => {
    if (readonly || !foodCatReady || !foodCatRef.current) return;
    const byId = new Map<number, Food>();
    for (const f of foodCatRef.current) byId.set(f.id, f);
    let dirty = false;
    const days = data.days.map((d) => {
      const meals = (d.meals || []).map((m) => {
        if (!m.items || m.items.length === 0) return m;
        let mealDirty = false;
        const items = m.items.map((it) => {
          if (!needsFoodFill(it)) return it;
          const f = byId.get(Number(it.libId));
          if (!f) return it;
          mealDirty = true;
          return {
            ...it,
            name: it.name || f.name,
            unit: it.unit || f.base_unit || "g",
            baseQty: f.base_quantity || 100,
            kcal: f.calories,
            protein: f.protein,
            carbs: f.carbs,
            fat: f.fat,
          };
        });
        if (!mealDirty) return m;
        dirty = true;
        return { ...m, items };
      });
      return meals === d.meals ? d : { ...d, meals };
    });
    if (dirty) onChange({ ...data, days });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, readonly, foodCatReady]);

  // Volcar totales de cada comida con items a sus campos string kcal/p/c/f.
  useEffect(() => {
    if (readonly) return;
    let dirty = false;
    const days = data.days.map((d) => {
      const meals = (d.meals || []).map((m) => {
        if (!m.items || m.items.length === 0) return m;
        const t = sumMealMacros(m.items);
        const kcal = String(r(t.kcal));
        const p = String(r(t.p));
        const c = String(r(t.c));
        const f = String(r(t.f));
        if (m.kcal === kcal && m.p === p && m.c === c && m.f === f) return m;
        dirty = true;
        return { ...m, kcal, p, c, f };
      });
      return meals === d.meals ? d : { ...d, meals };
    });
    if (dirty) onChange({ ...data, days });
  }, [data, readonly, onChange]);

  // Cuando la calculadora está activa y los datos son válidos, volcar el
  // objetivo calculado a data.macros (el "objetivo" que se ve arriba).
  useEffect(() => {
    if (readonly) return;
    if (!calc || !calc.enabled) return;
    const t = computeDietTargets(calc);
    if (!t.valid) return;
    const kcal = String(r(t.targetKcal));
    const protein = String(r(t.proteinG));
    const carbs = String(r(t.carbsG));
    const fat = String(r(t.fatG));
    const m = data.macros;
    if (m.kcal === kcal && m.protein === protein && m.carbs === carbs && m.fat === fat) return;
    onChange({ ...data, macros: { kcal, protein, carbs, fat } });
  }, [data, calc, readonly, onChange]);

  const updateMacro = (key: "kcal" | "protein" | "carbs" | "fat", v: string) => {
    onChange({ ...data, macros: { ...data.macros, [key]: v } });
  };

  const updateCalculator = (patch: Partial<DietCalculator>) => {
    if (!data.calculator) return;
    onChange({ ...data, calculator: { ...data.calculator, ...patch } });
  };

  const enableCalculator = () => {
    onChange({ ...data, calculator: defaultDietCalculator() });
  };

  const patchMeal = (di: number, mi: number, patch: Partial<DietMeal>) => {
    const days = [...data.days];
    const meals = [...days[di].meals];
    meals[mi] = { ...meals[mi], ...patch };
    days[di] = { ...days[di], meals };
    onChange({ ...data, days });
  };

  const updateDayLabel = (di: number, label: string) => {
    const days = [...data.days];
    days[di] = { ...days[di], label };
    onChange({ ...data, days });
  };

  const removeMeal = (di: number, mi: number) => {
    const days = [...data.days];
    const meals = [...days[di].meals];
    meals.splice(mi, 1);
    days[di] = { ...days[di], meals };
    onChange({ ...data, days });
  };

  const moveMeal = (di: number, mi: number, dir: -1 | 1) => {
    const days = [...data.days];
    const meals = [...days[di].meals];
    const target = mi + dir;
    if (target < 0 || target >= meals.length) return;
    [meals[mi], meals[target]] = [meals[target], meals[mi]];
    days[di] = { ...days[di], meals };
    onChange({ ...data, days });
  };

  const addMeal = (di: number, name: string = "Comida") => {
    const days = [...data.days];
    const meals = [
      ...days[di].meals,
      { id: genId("ml"), name, foods: "", kcal: "", p: "", c: "", f: "", items: [] },
    ];
    days[di] = { ...days[di], meals };
    onChange({ ...data, days });
  };

  /** Día cuyo menú de "+ Agregar comida" está abierto (uno a la vez). */
  const [addMealMenuFor, setAddMealMenuFor] = useState<number | null>(null);

  /** Modal de "Agregar día(s)" abierto / cerrado. */
  const [addDayOpen, setAddDayOpen] = useState(false);

  /** Inserta uno o varios días nuevos. Si se pasa `template` clona las comidas
   *  del primer día existente (útil para que el coach no tenga que rearmar
   *  Lunes-Domingo desde cero — duplica el "Día tipo" en cada uno). */
  const addDays = (labels: string[], copyFromFirst: boolean) => {
    if (labels.length === 0) return;
    const existing = data.days || [];
    const seed = copyFromFirst && existing[0] ? existing[0] : null;
    const newDays = labels
      // Evitar duplicados de label exacto (case-insensitive)
      .filter((lbl) => !existing.some((d) => (d.label || "").toLowerCase() === lbl.toLowerCase()))
      .map((label) => ({
        id: genId("dd"),
        label,
        meals: seed
          ? seed.meals.map((m) => ({
              ...m,
              id: genId("ml"),
              // Items con ids nuevos para no compartir referencias de styles
              items: (m.items || []).map((it) => ({ ...it, id: genId("fi") })),
            }))
          : [],
      }));
    if (newDays.length === 0) return;
    onChange({ ...data, days: [...existing, ...newDays] });
  };

  const removeDay = (di: number) => {
    if (data.days.length <= 1) return; // siempre al menos 1 día
    if (!confirm(`¿Eliminar "${data.days[di]?.label || `Día ${di + 1}`}" del plan?`)) return;
    const days = [...data.days];
    days.splice(di, 1);
    onChange({ ...data, days });
  };

  const moveDay = (di: number, dir: -1 | 1) => {
    const target = di + dir;
    if (target < 0 || target >= data.days.length) return;
    const days = [...data.days];
    [days[di], days[target]] = [days[target], days[di]];
    onChange({ ...data, days });
  };

  const addItem = (di: number, mi: number, food: Food) => {
    const meal = data.days[di].meals[mi];
    const items = [...(meal.items || []), foodToItem(food)];
    patchMeal(di, mi, { items });
  };

  const updateItemQty = (di: number, mi: number, itemId: string, qty: number) => {
    const meal = data.days[di].meals[mi];
    const items = (meal.items || []).map((it) =>
      it.id === itemId ? { ...it, qty: Number.isFinite(qty) && qty >= 0 ? qty : 0 } : it,
    );
    patchMeal(di, mi, { items });
  };

  const removeItem = (di: number, mi: number, itemId: string) => {
    const meal = data.days[di].meals[mi];
    const items = (meal.items || []).filter((it) => it.id !== itemId);
    patchMeal(di, mi, { items });
  };

  // El objetivo (kcal/macros) es POR DÍA, no por toda la semana. Calculamos
  // el promedio diario sumando todos los items y dividiendo por la cantidad de
  // días del plan — así un menú semanal con 7 días distintos se compara contra
  // el objetivo diario, no contra 7× el objetivo.
  const totalConsumed = sumConsumed(data);
  const dayCount = Math.max(1, data.days.length || 1);
  const consumed = {
    kcal: totalConsumed.kcal / dayCount,
    p: totalConsumed.p / dayCount,
    c: totalConsumed.c / dayCount,
    f: totalConsumed.f / dayCount,
  };
  // Objetivo para el progreso: el de la calculadora si está activa, si no los
  // valores (manuales) de data.macros.
  const goalKcal = targets && targets.valid ? targets.targetKcal : Number(data.macros.kcal) || 0;
  const goalP = targets && targets.valid ? targets.proteinG : Number(data.macros.protein) || 0;
  const goalC = targets && targets.valid ? targets.carbsG : Number(data.macros.carbs) || 0;
  const goalF = targets && targets.valid ? targets.fatG : Number(data.macros.fat) || 0;
  const hasGoal = goalKcal > 0 || goalP > 0 || goalC > 0 || goalF > 0;
  const calcActive = !!(calc && calc.enabled);

  return (
    <SectionPage readonly={readonly}>
      <PageHeader eyebrow="Plan nutricional" title="Plan nutricional" />

      {/* Botón "Generar dieta con IA" — solo para el coach. Doble guarda:
          callback opcional (no la pasan PrintablePlanView ni PublicPlanScreen)
          + chequeo de readonly + clase al-pb-coach-only para @media print. */}
      {onGenerateWithAi && !readonly && (
        <div className="al-section-ai-strip al-pb-coach-only">
          <button type="button" className="al-section-ai-btn" onClick={onGenerateWithAi}>
            <Icon name="sparkle" size={12} /> Generar dieta con IA
          </button>
        </div>
      )}

      {/* Calculadora nutricional (solo en el editor) */}
      {!readonly &&
        (calcActive && data.calculator ? (
          <DietCalculatorCard
            calc={data.calculator}
            targets={targets!}
            onChange={updateCalculator}
            onDisable={() => updateCalculator({ enabled: false })}
          />
        ) : (
          <button type="button" className="al-diet-calc-enable" onClick={enableCalculator}>
            <Icon name="trophy" size={14} />
            <span>
              <b>Activar calculadora nutricional</b>
              <span className="tiny muted" style={{ display: "block" }}>
                Calcula el objetivo de calorías y macros con el peso y el objetivo del cliente.
              </span>
            </span>
          </button>
        ))}

      {/* Objetivo (data.macros) — editable si la calculadora NO está activa */}
      <div className="al-macros">
        {MACRO_ROWS.map(([label, key]) => (
          <div className="al-macro" key={key}>
            <div className="al-macro-lbl">{label}</div>
            <div className="al-macro-val">
              {calcActive ? (
                <span>{data.macros[key] || "—"}</span>
              ) : (
                <InlineEditable
                  value={data.macros[key]}
                  onChange={(v) => updateMacro(key, v)}
                  readonly={readonly}
                  fieldPath={`macros.${key}`}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Progreso — consumido vs objetivo */}
      {hasGoal && (
        <DietProgress
          consumed={consumed}
          goal={{ kcal: goalKcal, p: goalP, c: goalC, f: goalF }}
          dayCount={dayCount}
        />
      )}

      <p className="al-pg-summary">
        <InlineEditable
          value={data.summary || ""}
          onChange={(v) => onChange({ ...data, summary: v })}
          multiline
          readonly={readonly}
          placeholder="Resumen nutricional…"
          fieldPath="summary"
        />
      </p>

      {data.days.map((day, di) => {
        const did = day.id || `idx${di}`;
        return (
          <div className="al-diet-day" id={day.id ? `day-${day.id}` : undefined} key={day.id || di}>
            <div className="al-diet-day-hd">
              <b>
                <InlineEditable
                  value={day.label}
                  onChange={(v) => updateDayLabel(di, v)}
                  readonly={readonly}
                  fieldPath={`day.${did}.label`}
                />
              </b>
              {!readonly && data.days.length > 1 && (
                <div className="al-diet-day-actions">
                  <button
                    type="button"
                    className="al-ex-move-btn"
                    onClick={() => moveDay(di, -1)}
                    disabled={di === 0}
                    title="Subir día"
                    aria-label="Subir día"
                  >↑</button>
                  <button
                    type="button"
                    className="al-ex-move-btn"
                    onClick={() => moveDay(di, 1)}
                    disabled={di === data.days.length - 1}
                    title="Bajar día"
                    aria-label="Bajar día"
                  >↓</button>
                  <button
                    type="button"
                    className="al-ex-del-btn"
                    onClick={() => removeDay(di)}
                    title="Eliminar día"
                  >
                    <Icon name="x" size={12} />
                  </button>
                </div>
              )}
            </div>
            {hasGoal && (
              <DayProgress
                consumed={sumDayMacros(day)}
                goal={{ kcal: goalKcal, p: goalP, c: goalC, f: goalF }}
              />
            )}

            {day.meals.map((m, mi) => {
              const mid = m.id || `idx${mi}`;
              const items = m.items || [];
              const mealTotals = items.length > 0 ? sumMealMacros(items) : null;
              return (
                <div className="al-diet-meal" key={m.id || mi}>
                  <div className="al-diet-meal-hd">
                    <b className="al-diet-meal-name">
                      <InlineEditable
                        value={m.name}
                        onChange={(v) => patchMeal(di, mi, { name: v })}
                        readonly={readonly}
                        fieldPath={`day.${did}.meal.${mid}.name`}
                      />
                    </b>
                    {mealTotals ? (
                      <span className="al-diet-meal-total">
                        {r(mealTotals.kcal)} kcal · {r(mealTotals.p)}P · {r(mealTotals.c)}C ·{" "}
                        {r(mealTotals.f)}G
                      </span>
                    ) : null}
                    {!readonly && (
                      <div className="al-ex-row-actions">
                        <button
                          type="button"
                          className="al-ex-move-btn"
                          onClick={() => moveMeal(di, mi, -1)}
                          disabled={mi === 0}
                          title="Subir comida"
                          aria-label="Subir"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="al-ex-move-btn"
                          onClick={() => moveMeal(di, mi, 1)}
                          disabled={mi === day.meals.length - 1}
                          title="Bajar comida"
                          aria-label="Bajar"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="al-ex-del-btn"
                          onClick={() => removeMeal(di, mi)}
                          title="Eliminar comida"
                        >
                          <Icon name="x" size={12} />
                        </button>
                      </div>
                    )}
                  </div>

                  {(items.length > 0 || !readonly) && (
                    <table className="al-pg-table al-diet-items">
                      <thead>
                        <tr>
                          <th>Alimento</th>
                          <th>Cantidad</th>
                          <th>Kcal</th>
                          <th>P</th>
                          <th>C</th>
                          <th>G</th>
                          {!readonly && <th></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it) => {
                          const sm = scaledItemMacros(it);
                          return (
                            <tr key={it.id}>
                              <td>{it.name}</td>
                              <td>
                                {readonly ? (
                                  <span>
                                    {it.qty} {it.unit}
                                  </span>
                                ) : (
                                  <span className="al-diet-qty">
                                    <input
                                      type="number"
                                      min={0}
                                      value={it.qty}
                                      onChange={(e) =>
                                        updateItemQty(di, mi, it.id, Number(e.target.value))
                                      }
                                    />
                                    <span className="al-diet-qty-unit">{it.unit}</span>
                                  </span>
                                )}
                              </td>
                              <td>{r(sm.kcal)}</td>
                              <td>{r(sm.p)}</td>
                              <td>{r(sm.c)}</td>
                              <td>{r(sm.f)}</td>
                              {!readonly && (
                                <td>
                                  <button
                                    type="button"
                                    className="al-ex-del-btn"
                                    onClick={() => removeItem(di, mi, it.id)}
                                    title="Quitar alimento"
                                  >
                                    <Icon name="x" size={12} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                        {items.length === 0 && !readonly && (
                          <tr>
                            <td colSpan={7} className="al-diet-items-empty">
                              Sin alimentos. Agregá desde el catálogo o escribilos
                              en las notas de abajo.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {!readonly && (
                    <button
                      type="button"
                      className="al-day-add-ex"
                      onClick={() => setPickerTarget({ di, mi })}
                    >
                      <Icon name="plus" size={12} /> Agregar alimento
                    </button>
                  )}

                  {items.length === 0 && (
                    <div className="al-diet-meal-macros">
                      {(
                        [
                          ["Kcal", "kcal"],
                          ["P", "p"],
                          ["C", "c"],
                          ["G", "f"],
                        ] as Array<[string, "kcal" | "p" | "c" | "f"]>
                      ).map(([lbl, key]) => (
                        <span className="al-diet-meal-macro" key={key}>
                          <span className="al-diet-meal-macro-lbl">{lbl}</span>
                          <InlineEditable
                            value={m[key]}
                            onChange={(v) => patchMeal(di, mi, { [key]: v })}
                            readonly={readonly}
                            fieldPath={`day.${did}.meal.${mid}.${key}`}
                          />
                        </span>
                      ))}
                    </div>
                  )}

                  {(m.foods || !readonly) && (
                    <div className="al-diet-meal-notes">
                      <InlineEditable
                        value={m.foods}
                        onChange={(v) => patchMeal(di, mi, { foods: v })}
                        multiline
                        readonly={readonly}
                        placeholder="Notas de la comida (opcional)…"
                        fieldPath={`day.${did}.meal.${mid}.foods`}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {!readonly && (
              <div className="al-diet-add-meal" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="al-day-add-ex"
                  onClick={() =>
                    setAddMealMenuFor(addMealMenuFor === di ? null : di)
                  }
                >
                  <Icon name="plus" size={12} /> Agregar comida
                </button>
                {addMealMenuFor === di && (
                  <div className="al-diet-add-meal-menu">
                    {STANDARD_MEAL_NAMES.filter(
                      (n) => !day.meals.some((m) => m.name === n),
                    ).map((name) => (
                      <button
                        key={name}
                        type="button"
                        className="al-diet-add-meal-chip"
                        onClick={() => {
                          addMeal(di, name);
                          setAddMealMenuFor(null);
                        }}
                      >
                        + {name}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="al-diet-add-meal-chip al-diet-add-meal-chip-other"
                      onClick={() => {
                        addMeal(di, "Comida");
                        setAddMealMenuFor(null);
                      }}
                    >
                      + Otra (personalizada)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!readonly && (
        // Botón "+ Agregar día" — antes el coach solo podía agregar comidas a
        // los días existentes y la plantilla nace con UN día ("Día tipo").
        // Esto era confuso porque el coach esperaba poder armar Lun-Dom.
        <button
          type="button"
          className="al-diet-add-day"
          onClick={() => setAddDayOpen(true)}
        >
          <Icon name="plus" size={14} /> Agregar día(s) al plan
        </button>
      )}

      {/* Resumen: menú semanal en tabla (Lun-Dom × comidas) — versión compacta
          ideal para imprimir y pegar en la heladera. */}
      <DietWeekTable data={data} />

      <FoodPickerModal
        open={pickerTarget !== null}
        onClose={() => setPickerTarget(null)}
        onPick={(food) => {
          if (pickerTarget) addItem(pickerTarget.di, pickerTarget.mi, food);
          setPickerTarget(null);
        }}
      />

      {addDayOpen && (
        <AddDayModal
          existingLabels={data.days.map((d) => d.label || "")}
          onClose={() => setAddDayOpen(false)}
          onAdd={(labels, copy) => {
            addDays(labels, copy);
            setAddDayOpen(false);
          }}
        />
      )}
    </SectionPage>
  );
}

// ----------------------------------------------------------------------
// AddDayModal — seleccionar día(s) de la semana a agregar al plan
// ----------------------------------------------------------------------

const WEEK_DAYS = [
  "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo",
] as const;

interface AddDayModalProps {
  existingLabels: string[];
  onClose: () => void;
  onAdd: (labels: string[], copyFromFirst: boolean) => void;
}

function AddDayModal({ existingLabels, onClose, onAdd }: AddDayModalProps) {
  // Set lowercased de los days ya en el plan — para deshabilitar los chips
  // que ya existen y evitar duplicados.
  const taken = new Set(existingLabels.map((l) => l.toLowerCase()));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [customName, setCustomName] = useState("");
  const [copyFromFirst, setCopyFromFirst] = useState(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (day: string) => {
    if (taken.has(day.toLowerCase())) return;
    const next = new Set(selected);
    if (next.has(day)) next.delete(day);
    else next.add(day);
    setSelected(next);
  };

  const selectAll = () => {
    const next = new Set<string>();
    WEEK_DAYS.forEach((d) => {
      if (!taken.has(d.toLowerCase())) next.add(d);
    });
    setSelected(next);
  };

  const clearSel = () => setSelected(new Set());

  const submit = () => {
    const labels: string[] = [];
    WEEK_DAYS.forEach((d) => {
      if (selected.has(d)) labels.push(d);
    });
    if (customName.trim()) labels.push(customName.trim());
    if (labels.length === 0) return;
    onAdd(labels, copyFromFirst);
  };

  const canSubmit = selected.size > 0 || customName.trim().length > 0;

  return (
    <div className="al-modal-backdrop" onClick={onClose}>
      <div
        className="al-modal al-diet-add-day-modal"
        role="dialog"
        aria-label="Agregar día(s)"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              Agregar día(s) al plan
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              Elegí uno o más días de la semana — vamos a armar uno por cada selección.
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="al-diet-add-day-body">
          <div className="al-diet-add-day-chips">
            {WEEK_DAYS.map((d) => {
              const isTaken = taken.has(d.toLowerCase());
              const isOn = selected.has(d);
              return (
                <button
                  key={d}
                  type="button"
                  className={
                    "al-diet-add-day-chip"
                    + (isOn ? " is-selected" : "")
                    + (isTaken ? " is-disabled" : "")
                  }
                  onClick={() => toggle(d)}
                  disabled={isTaken}
                  title={isTaken ? "Ya está en el plan" : undefined}
                >
                  {d}
                  {isTaken && <span className="al-diet-add-day-chip-tag">✓</span>}
                </button>
              );
            })}
          </div>

          <div className="al-diet-add-day-quick">
            <button type="button" className="link-btn" onClick={selectAll}>
              Toda la semana
            </button>
            <span className="muted" style={{ margin: "0 6px" }}>·</span>
            <button type="button" className="link-btn" onClick={clearSel}>
              Limpiar
            </button>
          </div>

          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>
              O agregá un día personalizado (opcional)
            </span>
            <input
              type="text"
              className="al-input"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Ej. Día de entreno, Día libre…"
            />
          </label>

          {existingLabels.length > 0 && (
            <label className="al-diet-add-day-copy">
              <input
                type="checkbox"
                checked={copyFromFirst}
                onChange={(e) => setCopyFromFirst(e.target.checked)}
              />
              <span>
                Copiar las comidas de <b>{existingLabels[0] || "el primer día"}</b> a cada nuevo día
                <span className="tiny muted" style={{ display: "block" }}>
                  Útil si querés armar Lun-Dom todos iguales para luego ajustar
                  diferencias por día.
                </span>
              </span>
            </label>
          )}
        </div>

        <div className="al-diet-add-day-foot">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={!canSubmit}
          >
            <Icon name="plus" size={14} /> Agregar {selected.size + (customName.trim() ? 1 : 0) || ""} día(s)
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// DietCalculatorCard — controles de la calculadora nutricional
// ----------------------------------------------------------------------

interface DietCalculatorCardProps {
  calc: DietCalculator;
  targets: ReturnType<typeof computeDietTargets>;
  onChange: (patch: Partial<DietCalculator>) => void;
  onDisable: () => void;
}

const ACTIVITY_OPTS: DietActivity[] = ["sedentary", "light", "moderate", "intense", "athlete"];
const ADJUST_MODES: DietAdjustMode[] = ["maintain", "deficit", "surplus"];
const ADJUST_PRESETS: DietAdjustPreset[] = ["light", "recommended", "aggressive", "custom"];
const PROTEIN_CHIPS = [1.5, 1.8, 2, 2.2];
const FAT_CHIPS = [0.5, 0.8, 1];

function DietCalculatorCard({ calc, targets, onChange, onDisable }: DietCalculatorCardProps) {
  return (
    <div className="al-diet-calc">
      <div className="al-diet-calc-hd">
        <span className="al-diet-calc-title">
          <Icon name="trophy" size={13} /> Calculadora nutricional
        </span>
        <button type="button" className="al-diet-calc-off" onClick={onDisable}>
          Desactivar
        </button>
      </div>

      {/* Datos antropométricos */}
      <div className="al-diet-calc-row">
        <label className="al-diet-calc-field">
          <span>Peso (kg)</span>
          <input
            type="number"
            min={0}
            value={calc.weightKg || ""}
            onChange={(e) => onChange({ weightKg: Number(e.target.value) })}
          />
        </label>
        <label className="al-diet-calc-field">
          <span>Altura (cm)</span>
          <input
            type="number"
            min={0}
            value={calc.heightCm || ""}
            onChange={(e) => onChange({ heightCm: Number(e.target.value) })}
          />
        </label>
        <label className="al-diet-calc-field">
          <span>Edad</span>
          <input
            type="number"
            min={0}
            value={calc.age || ""}
            onChange={(e) => onChange({ age: Number(e.target.value) })}
          />
        </label>
        <label className="al-diet-calc-field">
          <span>Sexo</span>
          <select
            value={calc.sex}
            onChange={(e) => onChange({ sex: e.target.value as "M" | "F" })}
          >
            <option value="M">Masculino</option>
            <option value="F">Femenino</option>
          </select>
        </label>
      </div>

      <div className="al-diet-calc-row">
        <label className="al-diet-calc-field grow">
          <span>Nivel de actividad</span>
          <select
            value={calc.activity}
            onChange={(e) => onChange({ activity: e.target.value as DietActivity })}
          >
            {ACTIVITY_OPTS.map((a) => (
              <option key={a} value={a}>
                {ACTIVITY_LABELS[a]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Objetivo + ajuste */}
      <div className="al-diet-calc-field">
        <span>Objetivo</span>
        <div className="al-diet-calc-seg">
          {ADJUST_MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              className={calc.adjustMode === mode ? "is-active" : ""}
              onClick={() => onChange({ adjustMode: mode })}
            >
              {ADJUST_MODE_LABELS[mode]}
            </button>
          ))}
        </div>
      </div>

      {calc.adjustMode !== "maintain" && (
        <div className="al-diet-calc-field">
          <span>Ajuste calórico</span>
          <div className="al-diet-calc-seg">
            {ADJUST_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={calc.adjustPreset === preset ? "is-active" : ""}
                onClick={() => onChange({ adjustPreset: preset })}
              >
                {ADJUST_PRESET_LABELS[preset]}
              </button>
            ))}
          </div>
          {calc.adjustPreset === "custom" && (
            <div className="al-diet-calc-custom">
              <input
                type="number"
                min={0}
                max={50}
                value={calc.adjustPct || ""}
                onChange={(e) => onChange({ adjustPct: Number(e.target.value) })}
              />
              <span className="tiny muted">
                % de {calc.adjustMode === "deficit" ? "déficit" : "superávit"}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Proteína / grasa por kg */}
      <div className="al-diet-calc-row">
        <div className="al-diet-calc-field grow">
          <span>Proteína (g por kg de peso)</span>
          <div className="al-diet-calc-perkg">
            <input
              type="number"
              min={0}
              step={0.1}
              value={calc.proteinPerKg || ""}
              onChange={(e) => onChange({ proteinPerKg: Number(e.target.value) })}
            />
            <div className="al-diet-calc-chips">
              {PROTEIN_CHIPS.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={calc.proteinPerKg === v ? "is-active" : ""}
                  onClick={() => onChange({ proteinPerKg: v })}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="al-diet-calc-field grow">
          <span>Grasa (g por kg de peso)</span>
          <div className="al-diet-calc-perkg">
            <input
              type="number"
              min={0}
              step={0.1}
              value={calc.fatPerKg || ""}
              onChange={(e) => onChange({ fatPerKg: Number(e.target.value) })}
            />
            <div className="al-diet-calc-chips">
              {FAT_CHIPS.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={calc.fatPerKg === v ? "is-active" : ""}
                  onClick={() => onChange({ fatPerKg: v })}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Resultado */}
      {targets.valid ? (
        <div className="al-diet-calc-result">
          <div className="al-diet-calc-result-main">
            <span>
              Gasto (TDEE) <b>{r(targets.tdee)}</b> kcal
            </span>
            <span className="al-diet-calc-result-target">
              Objetivo <b>{r(targets.targetKcal)}</b> kcal
              {targets.adjustPct !== 0 && (
                <span className="tiny">
                  {" "}
                  ({targets.adjustPct > 0 ? "+" : ""}
                  {targets.adjustPct}%)
                </span>
              )}
            </span>
          </div>
          <div className="al-diet-calc-result-macros">
            <span>
              Proteína <b>{r(targets.proteinG)}</b> g
            </span>
            <span>
              Carbos <b>{r(targets.carbsG)}</b> g
            </span>
            <span>
              Grasa <b>{r(targets.fatG)}</b> g
            </span>
          </div>
        </div>
      ) : (
        <div className="tiny muted" style={{ marginTop: 6 }}>
          Completá peso, altura y edad para calcular el objetivo.
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// DietProgress — barras de consumido vs objetivo
// ----------------------------------------------------------------------

interface DietProgressProps {
  consumed: { kcal: number; p: number; c: number; f: number };
  goal: { kcal: number; p: number; c: number; f: number };
  /** Cantidad de días en el plan — se muestra como contexto del promedio. */
  dayCount: number;
}

function DietProgress({ consumed, goal, dayCount }: DietProgressProps) {
  const rows: Array<{ label: string; unit: string; got: number; target: number }> = [
    { label: "Calorías", unit: "kcal", got: consumed.kcal, target: goal.kcal },
    { label: "Proteína", unit: "g", got: consumed.p, target: goal.p },
    { label: "Carbos", unit: "g", got: consumed.c, target: goal.c },
    { label: "Grasa", unit: "g", got: consumed.f, target: goal.f },
  ];
  return (
    <div className="al-diet-progress">
      <div className="al-diet-progress-title">
        Progreso del plan
        <span className="tiny muted">
          {" "}
          · promedio por día vs objetivo
          {dayCount > 1 ? ` (sobre ${dayCount} días)` : ""}
        </span>
      </div>
      {rows.map((row) => {
        const pct = row.target > 0 ? Math.min(100, (row.got / row.target) * 100) : 0;
        const over = row.target > 0 && row.got > row.target * 1.05;
        const done = row.target > 0 && row.got >= row.target * 0.95 && !over;
        return (
          <div className="al-diet-progress-row" key={row.label}>
            <span className="al-diet-progress-lbl">{row.label}</span>
            <div className="al-diet-progress-bar">
              <div
                className={
                  "al-diet-progress-fill" +
                  (over ? " is-over" : done ? " is-done" : "")
                }
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="al-diet-progress-val">
              {r(row.got)}
              <span className="muted"> / {r(row.target)} {row.unit}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------
// DayProgress — chips compactos con macros consumidos vs objetivo POR DÍA
// ----------------------------------------------------------------------

interface DayProgressProps {
  consumed: { kcal: number; p: number; c: number; f: number };
  goal: { kcal: number; p: number; c: number; f: number };
}

function DayProgress({ consumed, goal }: DayProgressProps) {
  if (goal.kcal <= 0 && goal.p <= 0 && goal.c <= 0 && goal.f <= 0) return null;
  const stats: Array<{ key: string; lbl: string; unit: string; got: number; target: number }> = [
    { key: "kcal", lbl: "Kcal", unit: "", got: consumed.kcal, target: goal.kcal },
    { key: "p", lbl: "Prot", unit: "g", got: consumed.p, target: goal.p },
    { key: "c", lbl: "Carb", unit: "g", got: consumed.c, target: goal.c },
    { key: "f", lbl: "Grasa", unit: "g", got: consumed.f, target: goal.f },
  ];
  return (
    <div className="al-day-progress">
      {stats.map((s) => {
        const over = s.target > 0 && s.got > s.target * 1.05;
        const done = s.target > 0 && s.got >= s.target * 0.95 && !over;
        const cls =
          "al-day-progress-stat" +
          (over ? " is-over" : done ? " is-done" : s.got > 0 ? " is-partial" : "");
        return (
          <div className={cls} key={s.key} title={`${s.lbl}: ${r(s.got)} / ${r(s.target)} ${s.unit}`}>
            <span className="al-day-progress-lbl">{s.lbl}</span>
            <span className="al-day-progress-val">
              {r(s.got)}
              <span className="al-day-progress-target"> / {r(s.target)}{s.unit ? ` ${s.unit}` : ""}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------
// DietWeekTable — menú semanal compacto: comidas (filas) × días (columnas)
// ----------------------------------------------------------------------

/** Formatea la cantidad de un alimento para la celda ("130 g" / "×2"). */
function fmtQty(it: DietFoodItem): string {
  const u = (it.unit || "g").toLowerCase();
  if (u === "g" || u === "ml") return `${it.qty} ${u}`;
  return `×${it.qty}`;
}

function DietCell({ meal }: { meal: DietMeal | undefined }) {
  if (!meal) return <span className="al-diet-wk-empty">—</span>;
  const items = meal.items || [];
  if (items.length > 0) {
    return (
      <ul className="al-diet-wk-items">
        {items.map((it) => (
          <li key={it.id}>
            {it.name} <span className="al-diet-wk-qty">{fmtQty(it)}</span>
          </li>
        ))}
      </ul>
    );
  }
  // Comida legacy sin items: mostramos el texto libre.
  return <span>{meal.foods || "—"}</span>;
}

function DietWeekTable({ data }: { data: DietData }) {
  const days = data.days || [];
  // Nombres de comida únicos, en orden de aparición — son las filas.
  const mealNames: string[] = [];
  for (const d of days) {
    for (const m of d.meals || []) {
      if (m.name && !mealNames.includes(m.name)) mealNames.push(m.name);
    }
  }
  if (days.length === 0 || mealNames.length === 0) return null;

  return (
    <div className="al-diet-wk">
      <div className="al-diet-wk-title">
        Menú semanal
        <span className="tiny muted"> · resumen para imprimir</span>
      </div>
      <div className="al-diet-wk-scroll">
        <table className="al-pg-table al-diet-wk-table">
          <thead>
            <tr>
              <th>Comida</th>
              {days.map((d, i) => (
                <th key={d.id || i}>{d.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mealNames.map((mn) => (
              <tr key={mn}>
                <td className="al-diet-wk-meal">{mn}</td>
                {days.map((d, i) => (
                  <td key={d.id || i}>
                    <DietCell meal={d.meals.find((m) => m.name === mn)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
